import { useImperativeHandle, useRef, useState } from 'react';
import { useLocation } from 'react-router';

import { createRequiredContext } from '../createRequiredContext';

export type IStepSubmitHandler = () => Promise<boolean | string>;

const [useOnboardingStepSubmit, OnboardingStepSubmitContext] = createRequiredContext<{
  handlerRef: React.RefObject<IStepSubmitHandler | null>;
  submitStep: () => Promise<boolean>;
  stepError: string | null;
  clearStepError: () => void;
}>();

const OnboardingStepSubmitProvider = ({ children }: React.PropsWithChildren) => {
  const handlerRef = useRef<IStepSubmitHandler>(null);
  const { pathname } = useLocation();
  const [error, setError] = useState<{ message: string; pathname: string } | null>(null);
  const stepError = error?.pathname === pathname ? error.message : null;

  const submitStep = async () => {
    setError(null);

    if (!handlerRef.current) return true;

    const result = await handlerRef.current();

    if (typeof result === 'string') setError({ message: result, pathname });

    return result === true;
  };

  const clearStepError = () => setError(null);

  const value = { handlerRef, submitStep, stepError, clearStepError };

  return <OnboardingStepSubmitContext value={value}>{children}</OnboardingStepSubmitContext>;
};

const useStepSubmitHandler = (handler: IStepSubmitHandler) => {
  const { handlerRef } = useOnboardingStepSubmit();

  useImperativeHandle(handlerRef, () => handler);
};

export { useOnboardingStepSubmit, useStepSubmitHandler, OnboardingStepSubmitProvider };
